import React, { useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, ImageIcon, X, Loader2, ExternalLink } from "lucide-react";

/**
 * Generic file uploader for Cloudinary-backed person uploads.
 * Renders a thumbnail (for photo) or a file-link card (for PDFs).
 *
 * Props:
 *  - kind: "photo" | "pan" | "aadhaar"
 *  - label: string
 *  - url: current secure_url (string)
 *  - onChange: (next: { url, public_id }) => void
 *  - testid: data-testid prefix
 */
export default function FileUploader({ kind, label = "file", url, onChange, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const isPhoto = kind === "photo" || kind === "news_event_photo" || kind === "social_impact_photo" || kind === "amenity_photo" || kind === "inst_home_feature_photo" || kind === "gallery_photo" || kind === "inst_gallery_photo" || kind === "inst_leader_photo" || kind === "inst_header_slide" || kind === "inst_logo" || kind === "trust_logo" || kind === "trust_hero_slide" || kind === "inst_home_image" || kind === "inst_facility_photo" || kind === "inst_alumni_photo" || kind === "inst_movable_photo" || kind === "vehicle_photo" || kind === "driver_photo";

  // For death_certificate (PDF or PNG), pick preview style at runtime from the URL
  const isImageByExt = (u) => /\.(png|jpe?g|webp)(\?|$)/i.test(u || "");
  const previewAsImage =
    isPhoto || ((kind === "death_certificate" || kind === "vehicle_document" || kind === "driver_document") && isImageByExt(url));
  const KIND_CONFIG = {
    photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 200 * 1024,
      humanMax: "200 KB",
      humanType: "JPG / PNG / WEBP",
    },
    news_event_photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 750 * 1024,
      humanMax: "750 KB",
      humanType: "JPG / PNG / WEBP",
    },
    social_impact_photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 200 * 1024,
      humanMax: "200 KB",
      humanType: "JPG / PNG / WEBP",
    },
    amenity_photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 200 * 1024,
      humanMax: "200 KB",
      humanType: "JPG / PNG / WEBP",
    },
    vehicle_photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 1024 * 1024,
      humanMax: "1 MB",
      humanType: "JPG / PNG / WEBP",
    },
    vehicle_document: {
      accept: "application/pdf,image/png,image/jpeg,image/jpg",
      maxBytes: 5 * 1024 * 1024,
      humanMax: "5 MB",
      humanType: "PDF / JPG / PNG",
    },
    driver_photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 1024 * 1024,
      humanMax: "1 MB",
      humanType: "JPG / PNG / WEBP",
    },
    driver_document: {
      accept: "application/pdf,image/png,image/jpeg,image/jpg",
      maxBytes: 5 * 1024 * 1024,
      humanMax: "5 MB",
      humanType: "PDF / JPG / PNG",
    },
    inst_home_feature_photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 1000 * 1024,
      humanMax: "1 MB",
      humanType: "JPG / PNG / WEBP",
    },
    pan: {
      accept: "application/pdf",
      maxBytes: 1024 * 1024,
      humanMax: "1 MB",
      humanType: "PDF only",
    },
    aadhaar: {
      accept: "application/pdf",
      maxBytes: 1024 * 1024,
      humanMax: "1 MB",
      humanType: "PDF only",
    },
    trust_document: {
      accept: "application/pdf",
      maxBytes: 25 * 1024 * 1024,
      humanMax: "25 MB",
      humanType: "PDF only",
    },
    property_document: {
      accept: "application/pdf",
      maxBytes: 25 * 1024 * 1024,
      humanMax: "25 MB",
      humanType: "PDF only",
    },
    gallery_photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 5 * 1024 * 1024,
      humanMax: "5 MB",
      humanType: "JPG / PNG / WEBP",
    },
    death_certificate: {
      accept: "application/pdf,image/png",
      maxBytes: 1024 * 1024,
      humanMax: "1 MB",
      humanType: "PDF or PNG",
    },
    employee_document: {
      accept: "application/pdf",
      maxBytes: 5 * 1024 * 1024,
      humanMax: "5 MB",
      humanType: "PDF only",
    },
    intake_document: {
      accept: "application/pdf",
      maxBytes: 10 * 1024 * 1024,
      humanMax: "10 MB",
      humanType: "PDF only",
    },
    fees_document: {
      accept: "application/pdf",
      maxBytes: 10 * 1024 * 1024,
      humanMax: "10 MB",
      humanType: "PDF only",
    },
    staff_photo: {
      accept: "image/jpeg,image/png,image/webp",
      maxBytes: 1 * 1024 * 1024,
      humanMax: "1 MB",
      humanType: "JPG / PNG / WEBP",
    },
    staff_resume: {
      accept: "application/pdf",
      maxBytes: 5 * 1024 * 1024,
      humanMax: "5 MB",
      humanType: "PDF only",
    },
    inst_gallery_photo: {
      accept: "image/png,image/jpeg,image/jpg,image/webp",
      maxBytes: 200 * 1024,
      humanMax: "200 KB",
      humanType: "JPG / PNG / WEBP",
    },
    inst_link_pdf: {
      accept: "application/pdf",
      maxBytes: 5 * 1024 * 1024,
      humanMax: "5 MB",
      humanType: "PDF only",
    },
    inst_notice_pdf: {
      accept: "application/pdf",
      maxBytes: 5 * 1024 * 1024,
      humanMax: "5 MB",
      humanType: "PDF only",
    },
    inst_leader_photo: {
      accept: "image/jpeg,image/png,image/webp,image/jpg",
      maxBytes: 200 * 1024,
      humanMax: "200 KB",
      humanType: "JPG / PNG / WEBP · 400×400 square",
    },
    inst_committee_pdf: {
      accept: "application/pdf",
      maxBytes: 10 * 1024 * 1024,
      humanMax: "10 MB",
      humanType: "PDF only",
    },
    inst_header_slide: {
      accept: "image/jpeg,image/png,image/webp,image/jpg",
      maxBytes: 500 * 1024,
      humanMax: "500 KB",
      humanType: "JPG / PNG / WEBP · 1920×620 recommended",
    },
    inst_logo: {
      accept: "image/jpeg,image/png,image/webp,image/jpg",
      maxBytes: 300 * 1024,
      humanMax: "300 KB",
      humanType: "JPG / PNG / WEBP · 400×400 square recommended",
    },
    trust_logo: {
      accept: "image/jpeg,image/png,image/webp,image/jpg",
      maxBytes: 300 * 1024,
      humanMax: "300 KB",
      humanType: "JPG / PNG / WEBP · 400×400 square recommended",
    },
    trust_hero_slide: {
      accept: "image/jpeg,image/png,image/webp,image/jpg",
      maxBytes: 300 * 1024,
      humanMax: "300 KB",
      humanType: "JPG / PNG / WEBP · 1200×1500 (4:5) recommended",
    },
    inst_home_image: {
      accept: "image/jpeg,image/png,image/webp,image/jpg",
      maxBytes: 800 * 1024,
      humanMax: "800 KB",
      humanType: "JPG / PNG / WEBP · 1280×720 (16:9) recommended",
    },
    inst_facility_photo: {
      accept: "image/jpeg,image/png,image/webp,image/jpg",
      maxBytes: 300 * 1024,
      humanMax: "300 KB",
      humanType: "JPG / PNG / WEBP",
    },
    inst_alumni_photo: {
      accept: "image/jpeg,image/png,image/webp,image/jpg",
      maxBytes: 300 * 1024,
      humanMax: "300 KB",
      humanType: "JPG / PNG / WEBP",
    },
    inst_movable_photo: {
      accept: "image/jpeg,image/jpg",
      maxBytes: 200 * 1024,
      humanMax: "200 KB",
      humanType: "JPG only",
    },
  };
  const cfg = KIND_CONFIG[kind] || KIND_CONFIG.pan;
  const accept = cfg.accept;
  const maxBytes = cfg.maxBytes;
  const humanMax = cfg.humanMax;
  const humanType = cfg.humanType;

  const pick = () => inputRef.current?.click();

  const handle = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    if (file.size > maxBytes) {
      toast.error(`${label} too large. Max ${humanMax}`);
      return;
    }

    const fd = new FormData();
    fd.append("kind", kind);
    fd.append("file", file);

    setBusy(true);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({
        url: data.url,
        public_id: data.public_id,
        resource_type: data.resource_type,
      });
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    onChange({ url: "", public_id: "", resource_type: "" });
  };

  return (
    <div className="flex flex-col gap-2" data-testid={`${testid}-wrapper`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
        {label}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handle}
        data-testid={`${testid}-input`}
      />

      {url ? (
        <div className="border border-[#e5e0d8] rounded-sm bg-white p-3 flex items-center gap-3">
          {previewAsImage ? (
            <img
              src={url}
              alt={label}
              className="w-14 h-14 rounded-sm object-cover border border-[#e5e0d8]"
              data-testid={`${testid}-preview`}
            />
          ) : (
            <div className="w-14 h-14 rounded-sm bg-[#f9f8f6] border border-[#e5e0d8] flex items-center justify-center text-[#234333]">
              <FileText size={22} strokeWidth={1.5} />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <div className="text-sm text-[#1c1a17] truncate">
              {label} uploaded
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testid}-view-link`}
              className="inline-flex items-center gap-1 text-xs text-[#234333] hover:underline mt-0.5"
            >
              View <ExternalLink size={11} strokeWidth={1.5} />
            </a>
          </div>

          <div className="flex gap-1 shrink-0">
            <button
              type="button"
              onClick={pick}
              disabled={busy}
              data-testid={`${testid}-replace`}
              className="text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm border border-[#e5e0d8]"
            >
              Replace
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              data-testid={`${testid}-remove`}
              className="text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm border border-[#e5e0d8]"
              aria-label="Remove"
            >
              <X size={12} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          data-testid={`${testid}-pick`}
          className="border border-dashed border-[#e5e0d8] hover:border-[#234333]/50 rounded-sm bg-white p-4 flex items-center gap-3 text-left transition-colors disabled:opacity-60"
        >
          <div className="w-10 h-10 rounded-sm bg-[#f9f8f6] border border-[#e5e0d8] flex items-center justify-center text-[#234333]">
            {busy ? (
              <Loader2 size={18} strokeWidth={1.5} className="animate-spin" />
            ) : isPhoto ? (
              <ImageIcon size={18} strokeWidth={1.5} />
            ) : (
              <Upload size={18} strokeWidth={1.5} />
            )}
          </div>
          <div className="flex-1">
            <div className="text-sm text-[#1c1a17]">
              {busy ? "Uploading…" : `Choose ${label.toLowerCase()}`}
            </div>
            <div className="text-[11px] text-[#6b655b]">
              {humanType} · max {humanMax}
            </div>
          </div>
        </button>
      )}
    </div>
  );
}
